import {
  Box,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ManyToOneSelect from "@/shared/forms/ManyToOneSelect";
import { User } from "@/types/types";
import dayjs from "dayjs";
import LabelWrapper from "../../root/LabelWrapper";
import { PositionEditor } from "../editors/PositionEditor";
import {
  Client,
  initialPosition,
  ProjectState,
  SitePosition,
} from "../Project";
import { usePersistantStore } from "@/stores/global";
import NewRangePicker from "@/shared/forms/NewRangePicker";
import { Label } from "@mui/icons-material";
import ManyToManySelect from "@/shared/forms/ManyToManySelect";
import { Crew } from "@/components/calendar/listView/CalendarListView";

export default function BaseDataPage({
  project,
  setField,
  setDateChangeNecessary,
}: {
  project: ProjectState;
  setDateChangeNecessary: any;
  setField: (changes: Partial<ProjectState>) => void;
}) {
  const region = usePersistantStore((store) => store.region);
  return (
    <Paper
      sx={{
        p: 0,
        flex: 1,
        display: "flex",
        overflow: "hidden",
        alignItems: "stretch",
      }}
    >
      <Box sx={{ minWidth: "400px", flex: 1, overflow: "auto" }}>
        <LabelWrapper label="Name">
          <TextField
            size="small"
            fullWidth
            value={project.name}
            placeholder="Name des Projektes"
            onChange={(e) =>
              setField({
                name: (e.target as HTMLInputElement).value,
              })
            }
          />
        </LabelWrapper>

        <LabelWrapper label="Kostenstelle">
          <TextField
            size="small"
            fullWidth
            value={project.number}
            placeholder="Kostenstelle"
            onChange={(e) =>
              setField({
                number: (e.target as HTMLInputElement).value,
              })
            }
          />
        </LabelWrapper>

        <LabelWrapper label="Geplante Bauzeit">
          <NewRangePicker
            small
            onChange={([startDate, endDate]) => {
              setField({
                plannedStart: !!startDate
                  ? startDate.format("YYYY-MM-DD")
                  : null,
                plannedEnd: !!endDate ? endDate.format("YYYY-MM-DD") : null,
              });
              setDateChangeNecessary(true);
            }}
            value={[
              !!project.plannedStart
                ? dayjs(project.plannedStart, "YYYY-MM-DD")
                : null,
              !!project.plannedEnd
                ? dayjs(project.plannedEnd, "YYYY-MM-DD")
                : null,
            ]}
          />
        </LabelWrapper>
        <LabelWrapper label="Status">
          <ToggleButtonGroup
            orientation="horizontal"
            exclusive
            value={project.active}
            onChange={(_, active) =>
              (typeof active === "boolean") &&
              setField({
                active,
              })
            }
          >
            <ToggleButton color="success" value={true}>
              Im Bau
            </ToggleButton>
            <ToggleButton color="error" value={false}>
              Abgeschlossen
            </ToggleButton>
          </ToggleButtonGroup>
        </LabelWrapper>
        <LabelWrapper label="Bauleiter">
          <ManyToOneSelect<User & { id: number }>
            size="small"
            disabled
            showWithoutChoice
            autoSelect={false}
            remoteFilter={{ region: ["region", project.region] }}
            entity={"user"}
            displayFunction={(o) =>
              !!o ? o.name + ", " + o.firstName : "(unbekannt)"
            }
            currentId={project.manager ?? 0}
            setId={(id) =>
              setField({
                manager: id > 0 ? id : null,
              })
            }
            placeholder={"Bauleiter"}
          />
        </LabelWrapper>
        <LabelWrapper label="Polier">
          <ManyToOneSelect<Crew & { id: number }>
            size="small"
            showWithoutChoice
            autoSelect={false}
            remoteFilter={{ region: ["region", project.region] }}
            entity={"crew"}
            displayFunction={(o) => (!!o ? o.name : "(unbekannt)")}
            currentId={project.polier ?? 0}
            setId={(id) =>
              setField({
                polier: id > 0 ? id : null,
              })
            }
            placeholder={"Polier"}
          />
        </LabelWrapper>
        <LabelWrapper label="Zusätzliche Benutzer">
          <ManyToManySelect<{ name: string; firstName: string; id: number }>
            sx={{ mt: 0, flex: 1 }}
            entity={"user"}
            currentIds={project.additionalUsers.content}
            setIds={(additionalUsers) => {
              if (additionalUsers.length === project.additionalUsers.content.length &&
                  additionalUsers.every((id, index) => id === project.additionalUsers.content[index])) return;
              setField({ additionalUsers: { changed: true, content: additionalUsers } });
            }}
          />
        </LabelWrapper>
        <LabelWrapper label="Auftraggeber">
          <ManyToOneSelect<Client & { id: number }>
            disabled
            size="small"
            entity={"client"}
            showWithoutChoice
            remoteFilter={{ region: ["region", project.region] }}
            autoSelect={false}
            displayFunction={(o) => (!!o ? o.name : "(unbekannt)")}
            currentId={project.client ?? 0}
            setId={(id) =>
              setField({
                client: id > 0 ? id : null,
              })
            }
            placeholder={"Auftraggeber"}
          />
        </LabelWrapper>
        <LabelWrapper label="Kommentar">
          <TextField
            size="small"
            fullWidth
            value={project.comment}
            multiline
            placeholder="Kommentar"
            onChange={(e) =>
              setField({
                comment: (e.target as HTMLInputElement).value,
              })
            }
          />
        </LabelWrapper>
      </Box>
      <Box
        id="mapContainer"
        sx={{
          flex: 10,
          borderLeft: "1px solid #aaa",
          position: "relative",
        }}
      >
        <PositionEditor
          position={project.position || initialPosition}
          onChange={(changes: Partial<SitePosition>) =>
            setField({
              position: {
                ...(project.position || initialPosition),
                ...changes,
              },
            })
          }
        />
      </Box>
    </Paper>
  );
}
